import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaTimes,
  FaEnvelope,
  FaGift,
  FaNewspaper,
  FaMusic,
  FaShoppingBag,
  FaCheck,
  FaExclamationTriangle,
} from "react-icons/fa";
import {
  ModalOverlay,
  ModalContainer,
  CloseButton,
  ModalContent,
  ModalHeader,
  EmailDisplay,
  PreferencesSection,
  PreferencesTitle,
  CheckboxGroup,
  CheckboxItem,
  CustomCheckbox,
  CheckboxContent,
  CheckboxIcon,
  ModalActions,
  ModalButton,
  ErrorMessage,
  FooterNote,
  SelectAllButton,
} from "./NewsletterModalStyles"; // Import your styled components
import useScrollLock from "../../hooks/useScrollLock";

const NewsletterPreferencesModal = ({
  isOpen,
  onClose,
  email,
  onSubscribe,
  loading = false,
  error = null,
}) => {
  const [preferences, setPreferences] = useState({
    newProducts: false,
    discounts: false,
    news: false,
    newBands: false,
  });

  const [allSelected, setAllSelected] = useState(false);

  const preferenceOptions = [
    {
      key: "newProducts",
      title: "Novi Proizvodi",
      description: "Obavesti me o novim majicama, dodacima i kolekcijama",
      icon: <FaShoppingBag />,
    },
    {
      key: "discounts",
      title: "Popusti i Akcije",
      description: "Ekskluzivni popusti, rasprodaje i specijalne ponude",
      icon: <FaGift />,
    },
    {
      key: "news",
      title: "Vesti i Najave",
      description: "Najnovije vesti iz sveta muzike i naše zajednice",
      icon: <FaNewspaper />,
    },
    {
      key: "newBands",
      title: "Novi Bendovi",
      description: "Obavesti me kada dodamo nove bendove u kolekciju",
      icon: <FaMusic />,
    },
  ];

  // Update allSelected state when preferences change
  useEffect(() => {
    const selectedCount = Object.values(preferences).filter(Boolean).length;
    setAllSelected(selectedCount === preferenceOptions.length);
  }, [preferences]);

  const handlePreferenceChange = (key) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSelectAll = () => {
    const newValue = !allSelected;
    const newPreferences = {};
    preferenceOptions.forEach((option) => {
      newPreferences[option.key] = newValue;
    });
    setPreferences(newPreferences);
  };

  const handleSubscribe = () => {
    const selectedPreferences = Object.keys(preferences).filter(
      (key) => preferences[key]
    );

    if (selectedPreferences.length === 0) {
      return; // Don't subscribe if no preferences selected
    }

    onSubscribe({
      email,
      preferences,
    });
    setPreferences({
      newProducts: false,
      discounts: false,
      news: false,
      newBands: false,
    });
  };

  const hasSelectedPreferences = Object.values(preferences).some(Boolean);

  useScrollLock(isOpen);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <ModalOverlay onClick={onClose}>
        <ModalContainer
          as={motion.div}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton onClick={onClose}>
            <FaTimes />
          </CloseButton>

          <ModalContent>
            <ModalHeader>
              <h3>
                <FaEnvelope />
                Podesi Preferencije
              </h3>
              <p>Izaberi koje vrste obaveštenja želiš da primaš</p>
            </ModalHeader>

            <EmailDisplay>
              <span>{email}</span>
            </EmailDisplay>

            {error && (
              <ErrorMessage>
                <FaExclamationTriangle />
                {error}
              </ErrorMessage>
            )}

            <PreferencesSection>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <PreferencesTitle>Šta te zanima?</PreferencesTitle>
                <SelectAllButton
                  onClick={handleSelectAll}
                  className={allSelected ? "selected" : ""}
                >
                  {allSelected ? "Poništi sve" : "Označi sve"}
                </SelectAllButton>
              </div>

              <CheckboxGroup>
                {preferenceOptions.map((option) => (
                  <CheckboxItem
                    key={option.key}
                    className={preferences[option.key] ? "checked" : ""}
                  >
                    <CustomCheckbox
                      type="checkbox"
                      checked={preferences[option.key]}
                      onChange={() => handlePreferenceChange(option.key)}
                    />
                    <CheckboxIcon>{option.icon}</CheckboxIcon>
                    <CheckboxContent>
                      <h5>{option.title}</h5>
                      <p>{option.description}</p>
                    </CheckboxContent>
                  </CheckboxItem>
                ))}
              </CheckboxGroup>
            </PreferencesSection>

            <ModalActions>
              <ModalButton className="secondary" onClick={onClose}>
                Otkaži
              </ModalButton>
              <ModalButton
                className="primary"
                disabled={!hasSelectedPreferences || loading}
                onClick={handleSubscribe}
              >
                {loading ? (
                  <>
                    <div
                      style={{
                        width: "16px",
                        height: "16px",
                        border: "2px solid transparent",
                        borderTop: "2px solid white",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                      }}
                    />
                    Kreiram...
                  </>
                ) : (
                  <>
                    <FaCheck />
                    Pretplati se
                  </>
                )}
              </ModalButton>
            </ModalActions>

            <FooterNote>
              <p>
                <strong>Privatnost:</strong> Tvoj email je siguran kod nas.
                Možeš se otkazati u bilo kom trenutku.
              </p>
            </FooterNote>
          </ModalContent>
        </ModalContainer>
      </ModalOverlay>

      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </AnimatePresence>
  );
};

export default NewsletterPreferencesModal;
